import { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { MoveHorizontal } from "lucide-react";
import { useLang } from "../context/LanguageContext";
import { RevealLine, RichLine } from "./Reveal";

const SHOTS = [
  { src: "/ba/masonry-after.jpg", h: "h-64 sm:h-80" },
  { src: "/videos/fireplace.jpg", h: "h-52 sm:h-64" },
  { src: "/ba/crown-after.jpg", h: "h-64 sm:h-80" },
  { src: "/videos/chimney-smoke.jpg", h: "h-52 sm:h-64" },
  { src: "/ba/fireplace-after.jpg", h: "h-64 sm:h-80" },
  { src: "/videos/masonry.jpg", h: "h-52 sm:h-64" },
  { src: "/videos/roofwork.jpg", h: "h-64 sm:h-80" },
  { src: "https://images.unsplash.com/photo-1781344334903-f33d8b76e292?crop=entropy&cs=srgb&fm=jpg&q=85&w=900", h: "h-52 sm:h-64" },
];

export const Gallery = () => {
  const { t } = useLang();
  const viewRef = useRef(null);
  const trackRef = useRef(null);
  const [bound, setBound] = useState(0);

  useEffect(() => {
    const measure = () => {
      if (!trackRef.current || !viewRef.current) return;
      setBound(Math.max(0, trackRef.current.scrollWidth - viewRef.current.offsetWidth + 40));
    };
    measure();
    window.addEventListener("resize", measure);
    return () => window.removeEventListener("resize", measure);
  }, []);

  return (
    <section data-testid="gallery-section" className="py-24 sm:py-32 overflow-hidden bg-[#faf9f6]">
      <div className="max-w-7xl mx-auto px-5 sm:px-8 text-center">
        <RevealLine>
          <span className="font-tech text-[11px] tracking-[0.4em] uppercase text-neutral-400">— {t.gallery.kicker}</span>
        </RevealLine>
        <h2 className="mt-6 font-display text-4xl sm:text-6xl lg:text-7xl leading-[1.06] tracking-tight text-neutral-900">
          {t.gallery.lines.map((line, i) => (
            <RevealLine key={i} delay={0.1 + i * 0.14}>
              <RichLine segments={line} />
            </RevealLine>
          ))}
        </h2>
      </div>

      <div ref={viewRef} className="mt-14 px-5 sm:px-8">
        <motion.div
          ref={trackRef}
          drag="x"
          dragConstraints={{ left: -bound, right: 0 }}
          dragTransition={{ power: 0.25, timeConstant: 220 }}
          className="flex items-start gap-5 sm:gap-8 w-max cursor-grab active:cursor-grabbing"
        >
          {SHOTS.map((s, i) => (
            <motion.figure
              key={i}
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.7, delay: (i % 4) * 0.08 }}
              className={`shrink-0 ${i % 2 ? "mt-10" : ""}`}
            >
              <div className={`${s.h} overflow-hidden rounded-xl border border-[#e8e5e0]`}>
                <img
                  src={s.src}
                  alt={`Emberline ${i + 1}`}
                  draggable={false}
                  className="h-full w-auto object-cover pointer-events-none transition-transform duration-700 hover:scale-[1.04]"
                />
              </div>
              <figcaption className="mt-3 font-tech text-[10px] tracking-[0.3em] uppercase text-neutral-400">
                Emberline — {String(i + 1).padStart(2, "0")}
              </figcaption>
            </motion.figure>
          ))}
        </motion.div>
        <p className="mt-10 flex items-center justify-center gap-2 font-tech text-[10px] tracking-[0.3em] uppercase text-neutral-400">
          <MoveHorizontal size={14} className="text-[#ff6b00]" /> {t.gallery.drag}
        </p>
      </div>
    </section>
  );
};
